/*
    * Created on Wed Oct 11 2023 4:46:23 PM
    *
    * ***************************************************************************************
    * Copyright (c) AVPS Technologies Inc 2023 
    *
    * All Rights Reserved
    * Unpublished rights are reserved under the copyright laws of the United States.
    * The software contained in this media is proprietary to and embodies confidential
    * the technology of AVPS Technologies Inc. Possession, use, duplication or
    * dissemination of the software and media is authorized only pursuant to a valid
    * written license from AVPS Technologies Inc.
    *
    * The above copyright notice and this permission notice shall be included in
    * all copies or substantial portions of the Software.
    *
    * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
    * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
    * 252.227-7013 or in FAR 52.227-19, as applicable.
    ****************************************************************************************
 */
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Repeat } from 'lucide-react';
import moment from 'moment-timezone';

const StepperInput = ({ value, onChange, min = 1, max = 365 }) => {
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [localVal, setLocalVal] = useState(String(value));
  const didFocusRef = useRef(false);

  useEffect(() => {
    if (!focused) setLocalVal(String(value));
  }, [value, focused]);

  const commit = (raw) => {
    const parsed = parseInt(raw, 10);
    const clamped = isNaN(parsed) ? value : Math.min(max, Math.max(min, parsed));
    onChange(clamped);
    setLocalVal(String(clamped));
  };

  const step = (delta, e) => {
    e.stopPropagation();
    const next = Math.min(max, Math.max(min, value + delta));
    onChange(next);
    setLocalVal(String(next));
  };

  const handleFocus = () => {
    setFocused(true);
    didFocusRef.current = true;
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.select();
      }
    });
  };

  const handleMouseUp = (e) => {
    if (didFocusRef.current) {
      e.preventDefault();
      inputRef.current?.select();
      didFocusRef.current = false;
    }
  };

  const handleBlur = (e) => {
    setFocused(false);
    didFocusRef.current = false;
    commit(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.currentTarget.blur(); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); step( 1, e); }
    if (e.key === 'ArrowDown') { e.preventDefault(); step(-1, e); }
  };

  const atMin = value <= min;
  const atMax = value >= max;

  const shell = {
    display: 'inline-flex', alignItems: 'center',
    border: focused ? '1.5px solid var(--flex-primary, #185FA5)' : '1px solid #cbd5e1',
    borderRadius: 7, overflow: 'hidden', height: 30,
    transition: 'border-color 0.15s',
    background: 'white',
  };
  const btnStyle = (disabled) => ({
    width: 28, height: '100%', border: 'none', background: '#f8fafc',
    cursor: disabled ? 'not-allowed' : 'pointer',
    color: disabled ? '#cbd5e1' : '#475569',
    fontSize: 17, lineHeight: 1, padding: 0, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.1s',
    userSelect: 'none',
  });

  return (
    <div style={shell} onClick={(e) => e.stopPropagation()}>
      <button type="button" style={{ ...btnStyle(atMin), borderRight: '1px solid #e2e8f0' }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => step(-1, e)} disabled={atMin} aria-label="Decrease">−</button>

      <input
        ref={inputRef}
        type="number"
        value={localVal}
        min={min} max={max}
        onFocus={handleFocus}
        onMouseUp={handleMouseUp}
        onBlur={handleBlur}
        onChange={(e) => setLocalVal(e.target.value)}
        onKeyDown={handleKeyDown}
        style={{
          width: 36, border: 'none', background: 'transparent',
          textAlign: 'center', fontSize: 13, fontWeight: 600,
          color: '#1e293b', outline: 'none', padding: 0, height: '100%',
          MozAppearance: 'textfield',
          borderLeft: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0',
        }}
      />

      <button type="button" style={{ ...btnStyle(atMax), borderLeft: '1px solid #e2e8f0' }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => step(1, e)} disabled={atMax} aria-label="Increase">+</button>
    </div>
  );
};

const RecurrenceModal = ({
  isOpen,
  onClose,
  selectedDate,
  selectedTime,
  onRecurrenceChange,
  existingRecurrenceData = null,
  workingDays = []
}) => {
  const [recurrenceOption, setRecurrenceOption] = useState(
    existingRecurrenceData?.recurrenceType?.toLowerCase() || 'daily'
  );
  const [recurrencePattern, setRecurrencePattern] = useState(
    existingRecurrenceData?.pattern || 'every'
  );
  const [recurrenceInterval, setRecurrenceInterval] = useState(
    existingRecurrenceData?.interval || 1
  );
  const [recurrenceEnds, setRecurrenceEnds] = useState(
    existingRecurrenceData?.ends || 'after'
  );
  const [occurrences, setOccurrences] = useState(
    existingRecurrenceData?.occurrences || 5
  );
  const [endDate, setEndDate] = useState(
    existingRecurrenceData?.endDate || moment(selectedDate).add(1, 'month').format('YYYY-MM-DD')
  );

  useEffect(() => {
    if (isOpen && existingRecurrenceData) {
      setRecurrenceOption(existingRecurrenceData.recurrenceType?.toLowerCase() || 'daily');
      setRecurrencePattern(existingRecurrenceData.pattern || 'every');
      setRecurrenceInterval(existingRecurrenceData.interval || 1);
      setRecurrenceEnds(existingRecurrenceData.ends || 'after');
      setOccurrences(existingRecurrenceData.occurrences || 5);
      setEndDate(existingRecurrenceData.endDate || moment(selectedDate).add(1, 'month').format('YYYY-MM-DD'));
    }
  }, [isOpen, existingRecurrenceData, selectedDate]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const formatTime12Hour = (time24) => {
    if (!time24) return '--:--';
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
  };

  const formatDate = (date) => moment(date).format('MMM DD, YYYY');

  const handleSave = () => {
    const recurrenceData = {
      recurrenceType: recurrenceOption.toUpperCase(),
      interval: recurrenceInterval,
      pattern: recurrencePattern,
      ends: recurrenceEnds,
      occurrences: recurrenceEnds === 'after' ? occurrences : null,
      endDate: recurrenceEnds === 'on' ? endDate : null
    };
    onRecurrenceChange(recurrenceData);
    onClose();
  };

  const handleCancel = () => onClose();

  // Get the appropriate suffix based on recurrence option
  const getPatternSuffix = () => {
    if (recurrenceOption === 'daily') {
      return 'working days';
    } else if (recurrenceOption === 'weekly') {
      return 'week(s)';
    } else {
      return 'month(s)';
    }
  };

  const freqIcons = {
    daily: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    weekly: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
    monthly: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      </svg>
    ),
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Repeat className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-[15px] font-semibold text-slate-900 leading-tight">
                  {existingRecurrenceData ? 'Edit recurrence' : 'Add recurrence'}
                </h2>
                <p className="text-[12px] text-slate-400 mt-0.5">
                  {formatDate(selectedDate)} · {formatTime12Hour(selectedTime?.start)}
                </p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleCancel}
              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 transition-colors"
            >
              <X className="w-3.5 h-3.5 text-slate-400" />
            </motion.button>
          </div>

          <div className="px-5 py-4 space-y-5">

            {/* Frequency Section */}
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Frequency</p>
              <div className="grid grid-cols-3 gap-2">
                {['daily', 'weekly', 'monthly'].map((option) => (
                  <motion.label
                    key={option}
                    whileTap={{ scale: 0.97 }}
                    className={`flex flex-col items-center gap-1.5 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
                      recurrenceOption === option
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <input type="radio" name="recurrenceOption" value={option} checked={recurrenceOption === option}
                      onChange={(e) => setRecurrenceOption(e.target.value)} className="sr-only" />
                    <span style={{ opacity: recurrenceOption === option ? 1 : 0.5 }}>
                      {freqIcons[option]}
                    </span>
                    <span className="text-[12px] font-medium capitalize">{option}</span>
                  </motion.label>
                ))}
              </div>
            </div>

            {/* Pattern Section */}
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Pattern</p>
              <div className="space-y-1.5">
                <label
                  onClick={() => setRecurrencePattern('every')}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
                    recurrencePattern === 'every' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <input type="radio" name="recurrencePattern" value="every" checked={recurrencePattern === 'every'}
                    onChange={(e) => setRecurrencePattern(e.target.value)} className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-[13px] text-slate-700 flex items-center gap-2">
                    Every
                    <StepperInput value={recurrenceInterval} onChange={setRecurrenceInterval} min={1} max={365} />
                    <span className="text-[12px] text-slate-500">{getPatternSuffix()}</span>
                  </span>
                </label>

                {/* Only show weekday option for daily recurrence */}
                {recurrenceOption === 'daily' && (
                  <motion.label
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    onClick={() => setRecurrencePattern('weekday')}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
                      recurrencePattern === 'weekday' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <input type="radio" name="recurrencePattern" value="weekday" checked={recurrencePattern === 'weekday'}
                      onChange={(e) => setRecurrencePattern(e.target.value)} className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-[13px] text-slate-700">Every weekday (Mon–Fri)</span>
                  </motion.label>
                )}
              </div>
            </div>

            {/* Range Section */}
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Range</p>

              {/* Starts row */}
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-100 mb-3">
                <span className="text-[12px] text-slate-500">Starts</span>
                <span className="text-[12px] font-medium text-slate-800">
                  {formatDate(selectedDate)}, {formatTime12Hour(selectedTime?.start)}
                </span>
              </div>

              {/* Ends label */}
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Ends</p>
              
              <div className="space-y-1.5">
                <label
                  onClick={() => setRecurrenceEnds('after')}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
                    recurrenceEnds === 'after' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <input type="radio" name="recurrenceEnds" value="after" checked={recurrenceEnds === 'after'}
                    onChange={(e) => setRecurrenceEnds(e.target.value)} className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-[13px] text-slate-700 flex items-center gap-2">
                    After
                    <StepperInput value={occurrences} onChange={setOccurrences} min={1} max={365} />
                    <span className="text-[12px] text-slate-500">occurrences</span>
                  </span>
                </label>

                <label
                  onClick={() => setRecurrenceEnds('on')}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
                    recurrenceEnds === 'on' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <input type="radio" name="recurrenceEnds" value="on" checked={recurrenceEnds === 'on'}
                    onChange={(e) => setRecurrenceEnds(e.target.value)} className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-[13px] text-slate-700 flex items-center gap-2">
                    On
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={moment(selectedDate).add(1, 'day').format('YYYY-MM-DD')}
                      className="border border-slate-300 rounded-md px-2 py-1 text-[12px] font-medium text-slate-700 focus:border-blue-500 focus:outline-none transition-colors"
                      style={{ height: 28 }}
                    />
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-slate-100 flex-shrink-0">
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleCancel}
              className="px-4 py-2 text-[13px] font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              Cancel
            </motion.button>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleSave}
              className="px-4 py-2 text-[13px] font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5">
              <Repeat className="w-3.5 h-3.5" />
              {existingRecurrenceData ? 'Update' : 'Add recurrence'}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RecurrenceModal;
